import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { EFFICIENCE_APP_URL } from '../../../environment/efficience'
import { dmpDomainActions, DmpRequestTypes } from '../../../store/domain/dmp'
import { IconButton, Modal } from '../../shared'
import { EffecienceIframeProps } from './EfficienceIframe.model'
import { isDefined } from '../../../misc/functions.utilities'
import { useActionDispatch } from '../../../hooks/utils'
import styles from './EfficienceIframe.module.scss'
import { useAtom } from 'jotai'
import { cpsCodeModalAtom } from '../../../state/vitalCard'
import { RESET } from 'jotai/utils'
import { isArray } from 'lodash'
import { developmentMode } from '../../../misc/environment.utilities'
import { parseEfficienceRequestId } from '../../../misc/dmp.utilities'
import { getCurrentPatient } from '../../../misc/currentPatient.utilities'

enum EfficienceErrorCode {
  NOT_FOUND = 'DMPNotFound',
  ERROR_CODE = 'DMPError',
  NO_HP = 'NoHPConnected',
  UNAUTHORIZED = 'RefusedAction',
}

export const EfficienceIframe: FunctionComponent<EffecienceIframeProps> = ({
  requests,
  iframeStatus,
  setIframeStatus,
  addValid,
  addError,
  updateDocument,
  updateFile,
  sendDmpMetrics,
  closeDmpSession,
}) => {
  const patient = getCurrentPatient()
  const efficienceRef = useRef<HTMLIFrameElement>(null)
  const [openEfficience, setOpenEfficience] = useState(false)
  const [, setCpsModalState] = useAtom(cpsCodeModalAtom)
  const removeRequest = useActionDispatch(dmpDomainActions.removeRequest)

  const sendMessageToEfficience = useCallback(() => {
    if (requests && requests.length > 0) {
      const request = requests[0]
      if (request.uuid && request.payload) {
        efficienceRef.current?.contentWindow?.postMessage(request.payload, '*')
      }
    }
  }, [requests])

  const handleClosePinCodeModal = useCallback(() => setCpsModalState(RESET), [setCpsModalState])

  const resetPinCodeModal = useCallback(() => {
    handleClosePinCodeModal()
    setIframeStatus('need_auth')
  }, [handleClosePinCodeModal, setIframeStatus])

  const handlePinCode = useCallback((pinCode: string | null) => {
    efficienceRef.current?.contentWindow?.postMessage(JSON.stringify({ pinCode }), '*')
  }, [])

  const openPinCodeModal = useCallback(async () => {
    const pinCodePromise = new Promise<string | null>((resolve, reject) => {
      const promiseCallback = (pincode: string | null) => resolve(pincode)
      setCpsModalState({ onPinCodeInput: promiseCallback })
    })

    const pincode = await pinCodePromise

    if (!isDefined(pincode)) {
      resetPinCodeModal()
      return
    }

    handlePinCode(pincode)
  }, [handlePinCode, resetPinCodeModal, setCpsModalState])

  const handleReturn = useCallback(() => {
    closeDmpSession()
    setOpenEfficience(false)
  }, [closeDmpSession])

  const reloadIframe = useCallback(() => {
    if (!efficienceRef.current) return

    efficienceRef.current.src = EFFICIENCE_APP_URL
    setIframeStatus('loading')
  }, [setIframeStatus])

  const handleDMPError = useCallback(
    (errors?: Array<{ code: string }>) => {
      const errorCodes = errors?.map((error) => error.code).filter(isDefined)

      if (
        errorCodes?.includes(EfficienceErrorCode.NO_HP) ||
        errorCodes?.includes(EfficienceErrorCode.UNAUTHORIZED)
      ) {
        reloadIframe()
      }

      let errorDetail = "Une erreur s'est produite"
      let titleError = "Erreur lors de l'envoi au DMP"

      if (errorCodes?.includes(EfficienceErrorCode.UNAUTHORIZED)) {
        errorDetail = 'Action non autorisée'
      }
      if (errorCodes?.includes(EfficienceErrorCode.NO_HP)) {
        errorDetail = 'Votre session a expiré'
      }

      if (errorCodes?.includes(EfficienceErrorCode.NOT_FOUND)) {
        titleError = "Erreur lors de l'ouverture du DMP"
        errorDetail = 'Ce patient ne possède pas de dossier médical partagé'
      }

      addError(titleError, errorDetail)
    },
    [addError, reloadIframe],
  )

  const handleIncorrectRequestId = useCallback(
    (receivedId: string) => {
      console.error(
        'Efficience error',
        'Unable to parse request id, received request Id :',
        receivedId,
      )
      addError('Erreur lié au DMP', 'Format de réponse incorrect')
    },
    [addError],
  )
  const shouldSendMetrics = (requestType: DmpRequestTypes): boolean => {
    return [
      DmpRequestTypes.SEND_DOCUMENT,
      DmpRequestTypes.SEND_DOCUMENT_PDF_FORM,
      DmpRequestTypes.SEND_FILES,
    ].includes(requestType)
  }
  const efficienceMessageHandler = useCallback(
    (event: MessageEvent) => {
      if (event.origin === EFFICIENCE_APP_URL) {
        const response = event.data

        if (response.data && requests && requests.length > 0) {
          const { answer } = JSON.parse(window.atob(response.data))
          if (developmentMode) {
            console.log(answer)
          }

          const parsedRequestId = parseEfficienceRequestId(answer.RelatesTo)
          if (!parsedRequestId) {
            handleIncorrectRequestId(answer.RelatesTo)
            return
          }
          removeRequest(parsedRequestId.fileId ?? undefined, requests)

          const hasError = isArray(answer?.errors) && answer.errors.length > 0
          if (hasError) {
            console.error('Efficience error', answer)
            handleDMPError(answer.errors)
            if (isDefined(patient?.id) && patient?.id && shouldSendMetrics(parsedRequestId.type)) {
              sendDmpMetrics({
                success: false,
                patientId: patient.id,
              })
            }
            return
          } else if (patient?.id && shouldSendMetrics(parsedRequestId.type)) {
            sendDmpMetrics({
              success: true,
              patientId: patient.id,
            })
          }

          switch (parsedRequestId.type) {
            case DmpRequestTypes.SEND_DOCUMENT:
            case DmpRequestTypes.SEND_DOCUMENT_PDF_FORM:
              addValid('Le document a bien été envoyé au dossier médical partagé')
              if (!isDefined(parsedRequestId.fileId))
                return handleIncorrectRequestId(answer.RelatesTo)
              updateDocument(parsedRequestId.fileId, {
                type: parsedRequestId.type === DmpRequestTypes.SEND_DOCUMENT ? 'farte' : 'pdf',
                dmpDocumentId: answer.document.uniqueId,
              })
              break

            case DmpRequestTypes.SEND_FILES:
              addValid('Le document a bien été envoyé au dossier médical partagé')
              if (!isDefined(parsedRequestId.fileId))
                return handleIncorrectRequestId(answer.RelatesTo)
              updateFile(parsedRequestId.fileId, {
                dmpId: answer.document.uniqueId,
              })
              break

            case DmpRequestTypes.DELETE_DOCUMENT:
              if (!isDefined(parsedRequestId.fileId))
                return handleIncorrectRequestId(answer.RelatesTo)
              updateDocument(parsedRequestId.fileId, { dmpDocumentId: null })
              addValid('Le document a été supprimé du dossier médical partagé')
              break

            case DmpRequestTypes.DELETE_FILE:
              if (!isDefined(parsedRequestId.fileId))
                return handleIncorrectRequestId(answer.RelatesTo)
              updateFile(parsedRequestId.fileId, { dmpId: null })
              addValid('Le fichier a été supprimé du dossier médical partagé')
              break

            case DmpRequestTypes.OPEN_DMP:
              if (answer?.errors?.every(({ code }) => code === EfficienceErrorCode.NOT_FOUND)) {
                addError('Ce patient ne possède pas de dossier médical partagé')
              } else {
                setOpenEfficience(true)
              }
              break
          }
          // Metrics
        } else {
          if (response.type === 'login') {
            setOpenEfficience(false)
            handleClosePinCodeModal()
            sendMessageToEfficience()
          }

          if (response.type === 'waiting_for_pincode_entry') {
            setIframeStatus('need_auth')
          }

          if (response.type === 'waiting_for_practice_location') {
            setOpenEfficience(true)
            handleClosePinCodeModal()
            sendMessageToEfficience()
          }

          if (response.type === 'login_error') {
            addError('Code PIN CPS invalide', 'Veuillez réessayer')
            resetPinCodeModal()
          }

          if (response.type === 'login' && isDefined(response?.hpInfos)) {
            setIframeStatus('ready')
          }
        }
      }
    },
    [
      requests,
      removeRequest,
      handleDMPError,
      handleIncorrectRequestId,
      addError,
      addValid,
      updateDocument,
      updateFile,
      handleClosePinCodeModal,
      sendMessageToEfficience,
      setIframeStatus,
      resetPinCodeModal,
      patient?.id,
      sendDmpMetrics,
    ],
  )

  useEffect(() => {
    if (iframeStatus === 'auth_in_progress') {
      openPinCodeModal()
    }
  }, [iframeStatus, openPinCodeModal])

  useEffect(() => {
    window.addEventListener('message', efficienceMessageHandler)
    return () => {
      window.removeEventListener('message', efficienceMessageHandler)
    }
  }, [efficienceMessageHandler])

  useEffect(() => {
    sendMessageToEfficience()
  }, [sendMessageToEfficience])

  return (
    <>
      <Modal
        onClickOutside={() => setOpenEfficience(false)}
        isUnmounting={false}
        hidden={!openEfficience}
      >
        <div className={styles.container}>
          <div className={styles.backWrapper}>
            <IconButton
              onClick={handleReturn}
              icon="back"
              theme="light"
              size="normal"
              testId="button-efficience_iframe-exit"
            />
            <span className={styles.backReturnText}>Retour sur Follow</span>
          </div>
          <iframe
            allow="camera; loopback-network *; local-network *; local-network-access *"
            className={styles.efficienceIframeContent}
            ref={efficienceRef}
            title="efficience-iframe"
            src={EFFICIENCE_APP_URL}
          />
        </div>
      </Modal>
    </>
  )
}
